import type { ImpactDay } from '@tabel/shared/schemas/rules';
import { Button, Modal } from '../../components/ui';
import { ruDate } from '../../lib/format';
import { PinIconButton, usePin } from './pin';
import { LABEL, RESULT_TEXT, T, hoursText, say } from './texts';

/*
 * Дни влияния (004, T065, T067; contracts/ui.md, «Окна» → «В каких днях изменятся часы»; FR-029): человек, дата,
 * часы ячейки было → станет, вопросы бригадиру, правка. Часы — те, что прислал сервер: интерфейс их не считает и не
 * складывает; у дня с несколькими строками табеля — по строкам.
 */

/** Сколько дней показывает окно (contracts/ui.md: до 200 строк). */
export const DAYS_LIMIT = 200;

const STATE_WORD: Readonly<Record<string, string>> = {
  нет_выхода: RESULT_TEXT.noExit,
  no_exit: RESULT_TEXT.noExit,
  нет_входа: RESULT_TEXT.noEntry,
  no_entry: RESULT_TEXT.noEntry,
  на_объекте: RESULT_TEXT.onSite,
  on_site: RESULT_TEXT.onSite,
  over_24h: RESULT_TEXT.over24,
  error: RESULT_TEXT.error,
};

/** Сторона дня: «8 ч», по строкам — «Я 8, Н 2»; без часов — состояние словами. */
export function sideText(side: ImpactDay['before']): string {
  const word = STATE_WORD[side.state];
  if (word) return word;
  const cells = Object.entries(side.cells);
  if (cells.length === 0) return side.state === 'пусто' || side.state === 'empty' ? RESULT_TEXT.empty : '—';
  if (cells.length === 1) return `${hoursText(cells[0]?.[1] ?? 0)} ч`;
  return cells.map(([row, h]) => `${row} ${hoursText(h)}`).join(', ');
}

/** Вопросы бригадиру: появится, исчезнет. */
export function questionsText(q: ImpactDay['questions']): string {
  return [q.plus.length > 0 ? T.questionPlus : '', q.minus.length > 0 ? T.questionMinus : '']
    .filter(Boolean)
    .join(', ');
}

/**
 * Строка дня: человек, дата, было → станет, вопросы, правка. Без `onOpen` — только текст. Администратору правил — ещё
 * «Сделать проверочным примером» (FR-051).
 */
export function DayRow({ day, onOpen }: { day: ImpactDay; onOpen?: () => void }) {
  const pin = usePin();
  const q = questionsText(day.questions);
  const body = (
    <span className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-0.5 text-left sm:grid-cols-[minmax(0,1fr)_96px_minmax(120px,auto)]">
      <span className="min-w-0 truncate font-medium text-ink">{day.name}</span>
      <span className="text-ink-2 tabular-nums sm:order-none">{ruDate(day.date)}</span>
      <span className="col-span-2 tabular-nums sm:col-span-1">
        {sideText(day.before)} → <b className="font-semibold text-ink">{sideText(day.after)}</b>
        {q && <span className="text-ink-2">{` · ${q}`}</span>}
        {day.edited && <span className="text-warn">{` · ${LABEL.editedMark}`}</span>}
      </span>
    </span>
  );
  return (
    <li className="flex items-center border-b border-line last:border-b-0">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="flex min-h-10 min-w-0 flex-1 items-center px-3 py-2 text-[13.5px] transition-colors hover:bg-subtle"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-h-10 min-w-0 flex-1 items-center px-3 py-2 text-[13.5px]">{body}</div>
      )}
      {pin && (
        <PinIconButton onClick={() => pin({ personId: day.personId, name: day.name, date: day.date })} />
      )}
    </li>
  );
}

/** Окно «В каких днях изменятся часы»: до 200 строк, сверху — «Показаны первые 200». */
export function DaysDialog({
  open,
  days,
  onClose,
}: {
  open: boolean;
  days: readonly ImpactDay[];
  onClose: () => void;
}) {
  const shown = days.slice(0, DAYS_LIMIT);
  return (
    <Modal open={open} onClose={onClose} title={LABEL.daysTitle} width="w-[720px]">
      {days.length > DAYS_LIMIT && <p className="mb-2 text-[13px] text-ink-2">{say.daysFirst(DAYS_LIMIT)}</p>}
      <ul
        aria-label={LABEL.daysTitle}
        className="max-h-[60dvh] overflow-y-auto rounded-control border border-line"
      >
        {shown.map((d) => (
          <DayRow key={`${d.personId}|${d.date}`} day={d} />
        ))}
      </ul>
      <div className="mt-5 flex justify-end">
        <Button onClick={onClose}>{LABEL.close}</Button>
      </div>
    </Modal>
  );
}
